import { Box, IconButton } from '@mui/material';
import React, { useCallback, useState } from 'react';
import Export from './Export';
import { ParagraphSpacing as SolidRuler } from '@untitledui-pro/icons/solid';
import { Compass03 as LineCompass, Expand06, Minimize02, Download01 } from '@untitledui-pro/icons/line';
import ContextDialog from '../Context';
import Camera from '../Camera';
import EditGeodata from '../EditGeodata';
import SidePanelDialog from './SidePaneDialog';
import { HeaderDialogType, Panels, SideDialog } from '@gui/constants/Panels';
import { Camera01, Globe06, Image03, Copy07 } from '@untitledui-pro/icons/line';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import usePersistentStore, { useGlobalBucket } from '@gui/store/persistent/persistentStore';
import State from '../State/State';

const LeftSettings: React.FC = ({}) => {
    const legendIsVisible = useGlobalBucket((s) => s.overlays.legendVisible);
    const compassIsVisible = useGlobalBucket((s) => s.overlays.compassVisible);
    const [activeDialog, setActiveDialog] = useState<HeaderDialogType | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const setPanel = usePersistentStore((s) => s.setPanel);

    const onToggleFullscreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
            setIsFullscreen(false);
        } else {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        }
    };

    const onToggleLegend = useCallback(() => {
        setPanel({ legendVisible: !legendIsVisible }, Panels.OVERLAYS);
    }, [legendIsVisible, setPanel]);

    const onToggleCompass = useCallback(() => {
        setPanel({ compassVisible: !compassIsVisible }, Panels.OVERLAYS);
    }, [compassIsVisible, setPanel]);

    const leftActions: SideDialog[] = [
        {
            type: HeaderDialogType.CONTEXT,
            Icon: <Image03 className="icon-size-big" />,
            title: 'CONTEXT',
            tooltip: 'Context',
            content: <ContextDialog showContent={activeDialog === HeaderDialogType.CONTEXT} />,
            btnID: 'contextBtn',
        },
        {
            type: HeaderDialogType.CAMERA,
            Icon: <Camera01 className="icon-size-big" />,
            title: 'CAMERAS',
            tooltip: 'Cameras',
            content: <Camera showContent={activeDialog === HeaderDialogType.CAMERA} />,
            btnID: 'cameraBtn',
        },
        {
            type: HeaderDialogType.COMPASS,
            Icon: compassIsVisible ? (
                <LineCompass className="icon-size-big" style={{ opacity: 1 }} />
            ) : (
                <LineCompass className="icon-size-big" style={{ opacity: 0.45, strokeWidth: 1.2 }} />
            ),
            title: 'COMPASS',
            tooltip: 'Compass',
            onClick: onToggleCompass,
            btnID: 'compassBtn',
        },
        {
            type: HeaderDialogType.LEGEND,
            Icon: (
                <SolidRuler
                    className="icon-size-big"
                    style={{
                        transform: 'rotate(90deg)',
                        opacity: legendIsVisible ? 1 : 0.45,
                        strokeWidth: legendIsVisible ? 2.2 : 1.2,
                        fill: legendIsVisible ? 'currentColor' : 'none',
                    }}
                />
            ),
            title: 'LEGEND',
            tooltip: 'Legend',
            onClick: onToggleLegend,
            btnID: 'legendBtn',
        },
        {
            type: HeaderDialogType.EDIT_GEODATA,
            Icon: <Globe06 className="icon-size-big" />,
            title: 'EDIT GEODATA',
            tooltip: 'Edit Geodata',
            content: <EditGeodata showContent={activeDialog === HeaderDialogType.EDIT_GEODATA} />,
            btnID: 'editGeodataBtn',
        },
        {
            type: HeaderDialogType.EXPORT,
            Icon: <Download01 className="icon-size-big" />,
            title: 'EXPORT',
            tooltip: 'Export',
            content: <Export showContent={activeDialog === HeaderDialogType.EXPORT} />,
            btnID: 'exportBtn',
        },
        {
            type: HeaderDialogType.STATE,
            Icon: <Copy07 className="icon-size-big" />,
            title: 'STATE',
            tooltip: 'State',
            content: <State showContent={activeDialog === HeaderDialogType.STATE} />,
            btnID: 'stateBtn',
        },
        {
            type: HeaderDialogType.FULL_SCREEN,
            Icon: isFullscreen ? <Minimize02 className="icon-size-big" /> : <Expand06 className="icon-size-big" />,
            title: 'FULL SCREEN',
            tooltip: isFullscreen ? 'Disable full screen' : 'Enable full screen',
            onClick: onToggleFullscreen,
            btnID: 'fullscreenBtn',
        },
    ];

    const handleDialogClick = (dialog: SideDialog) => {
        if (dialog.onClick) {
            dialog.onClick();
            return;
        }

        if (dialog.content) {
            setActiveDialog((prev) => (prev === dialog.type ? null : dialog.type));
        }
    };

    const closeDialog = useCallback(() => {
        setActiveDialog(null);
    }, []);

    return (
        <Box className="column-icons column-icons--left" style={{ gridTemplateColumns: `repeat(${leftActions.length}, minmax(0, 1fr))` }}>
            {leftActions.map((dialog) => {
                const icon = (
                    <IconButton id={dialog.btnID} size="small" onClick={() => handleDialogClick(dialog)} aria-label={dialog.title}>
                        {dialog.Icon}
                    </IconButton>
                );

                return (
                    <Box key={dialog.type} sx={{ display: 'flex', justifyContent: 'center' }}>
                        {dialog.tooltip ? (
                            <CustomTooltip side="left" title={dialog.tooltip}>
                                {icon}
                            </CustomTooltip>
                        ) : (
                            icon
                        )}
                    </Box>
                );
            })}

            <SidePanelDialog
                open={activeDialog}
                onClose={closeDialog}
                title={leftActions.find((dialog) => dialog.type === activeDialog)?.title}
                side={'left'}
            >
                {leftActions.map((dialog) => (dialog.content ? <React.Fragment key={dialog.type}>{dialog.content}</React.Fragment> : null))}
            </SidePanelDialog>
        </Box>
    );
};

export default LeftSettings;
